'use client';

import dynamic from 'next/dynamic';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import logTrailDetailsOverviewLinkClicked from '@alltrails/amplitude/events/logTrailDetailsOverviewLinkClicked';
import type TrailDetails from '@alltrails/types/TrailDetails';
import Button from '@alltrails/denali/components/Button';
import IconContainer from '@alltrails/denali/components/IconContainer';
import TableRow from '@alltrails/denali/components/TableRow';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import Caution from '@alltrails/denali/icons/Caution';
import ChevronSmall from '@alltrails/denali/icons/ChevronSm';
import type { TrailInfo } from '@alltrails/di-tooling';
import type { Group } from '@alltrails/groups';
import { AlertType } from '@alltrails/types/Alert';
import isClosureOrDangerAlertActive from '../../utils/isClosureOrDangerAlertActive';
import styles from './AlertsButton.module.scss';

const AlertsModal = dynamic(() => import('@/components/AlertsModal/AlertsModal'), { ssr: false });

type AlertsButtonProps = {
  isAdmin?: boolean;
  isPLPUser?: boolean;
  trailDetails: TrailDetails;
  trailInfo?: TrailInfo;
  userGroups?: Group[];
};

const AlertsButton = ({ isAdmin, isPLPUser, trailDetails, trailInfo, userGroups }: AlertsButtonProps) => {
  const intl = useIntl();
  const isMobile = useIsMobileSizedScreen();
  const [isAlertsModalOpen, setIsAlertsModalOpen] = useState(false);

  const hasAlerts =
    trailDetails.alerts &&
    trailDetails.alerts.length > 0 &&
    trailDetails.alerts.every(
      alert => alert.alertType === AlertType.INFORMATION || alert.alertType === AlertType.WARNING || !isClosureOrDangerAlertActive(alert)
    );

  if (!hasAlerts) {
    return null;
  }

  const onAlertsButtonClick = () => {
    setIsAlertsModalOpen(true);
    logTrailDetailsOverviewLinkClicked({ overview_link_clicked: 'alert', trail_id: trailDetails.id });
  };

  const alertsButtonText = intl.formatMessage(
    { defaultMessage: '{count, plural, one {# alert} other {# alerts}}' },
    { count: trailDetails.alerts?.length }
  );

  return (
    <>
      {isMobile ? (
        <div className={styles.tableRow}>
          <button className={styles.mobileAlertsButton} onClick={onAlertsButtonClick} type="button">
            <TableRow
              className={styles.row}
              label={alertsButtonText}
              leftContent={
                <IconContainer className={styles.icon}>
                  <Caution />
                </IconContainer>
              }
              rightContent={<ChevronSmall orientation="right" />}
            />
          </button>
        </div>
      ) : (
        <Button className={styles.desktopAlertsButton} icon={{ Component: Caution }} onClick={onAlertsButtonClick} text={alertsButtonText} />
      )}
      {isAlertsModalOpen && (
        <AlertsModal
          isAdmin={isAdmin}
          isPLPUser={isPLPUser}
          onClose={() => setIsAlertsModalOpen(false)}
          trailDetails={trailDetails}
          trailInfo={trailInfo}
          userGroups={userGroups}
        />
      )}
    </>
  );
};

export default AlertsButton;
